import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { RuntimeProvider, useLive, useRuntime, useUser } from './context';
import type { Runtime } from '../runtime';
import { StatusStrip } from './StatusStrip';
import { SignIn } from './SignIn';
import { Today } from './Today';
import { CalendarView } from './CalendarView';
import { Clients } from './Clients';
import { Settings } from './Settings';
import { Tasks } from './Tasks';

/**
 * The shell: LoveSpa's shape, because that shape works on an iPad behind a
 * spotty connection (spec §6.2).
 *
 * Tabs rather than a router, and no router dependency. The app has five screens
 * and no deep links — an installed PWA on a counter is opened by tapping its
 * icon, not by pasting a URL — so a route table would be a dependency and a
 * bundle for a `useState`.
 *
 * **Tasks is a tab, not a modal.** §8.3's rejected intents are the owner's work
 * queue, and burying them behind a toast that can be missed would be the same
 * mistake as merging them: the honest cost of the model has to be somewhere the
 * owner can see it, count it, and come back to it.
 */

export type Tab = 'today' | 'calendar' | 'clients' | 'tasks' | 'settings';

export function App({ runtime }: { runtime: Runtime }): ReactNode {
  return (
    <RuntimeProvider runtime={runtime}>
      <Authenticated />
    </RuntimeProvider>
  );
}

function Authenticated(): ReactNode {
  const runtime = useRuntime();
  const user = useUser();

  useEffect(() => {
    if (user === null) {
      runtime.sync.stop();
      return;
    }
    runtime.sync.start();

    // The radio came back. `online` is a hint rather than a guarantee — a
    // captive portal fires it too — so it only shortcuts the backoff; the cycle
    // itself decides whether there is really a server there.
    const wake = (): void => runtime.sync.wake();
    globalThis.addEventListener?.('online', wake);
    return () => {
      globalThis.removeEventListener?.('online', wake);
    };
  }, [runtime, user]);

  if (user === null) return <SignIn auth={runtime.auth} />;
  return <Shell />;
}

function Shell(): ReactNode {
  const runtime = useRuntime();
  const [tab, setTab] = useState<Tab>('today');
  const taskCount = useLive(async () => (await runtime.outbox.rejected()).length, 0);

  return (
    <div className="app">
      <StatusStrip onSignOut={() => void runtime.signOut()} />

      <main className="app__body">
        {tab === 'today' ? <Today /> : null}
        {tab === 'calendar' ? <CalendarView /> : null}
        {tab === 'clients' ? <Clients /> : null}
        {tab === 'tasks' ? <Tasks /> : null}
        {tab === 'settings' ? <Settings /> : null}
      </main>

      <nav className="tabs" aria-label="Sections">
        <TabButton current={tab} id="today" label="Today" onPick={setTab} />
        <TabButton current={tab} id="calendar" label="Calendar" onPick={setTab} />
        <TabButton current={tab} id="clients" label="Clients" onPick={setTab} />
        <TabButton current={tab} id="tasks" label="Tasks" onPick={setTab} count={taskCount} />
        <TabButton current={tab} id="settings" label="Settings" onPick={setTab} />
      </nav>
    </div>
  );
}

function TabButton({
  current,
  id,
  label,
  onPick,
  count,
}: {
  current: Tab;
  id: Tab;
  label: string;
  onPick: (tab: Tab) => void;
  count?: number;
}): ReactNode {
  return (
    <button
      type="button"
      className={`tabs__tab${current === id ? ' is-current' : ''}`}
      aria-current={current === id ? 'page' : undefined}
      onClick={() => onPick(id)}
    >
      {label}
      {count !== undefined && count > 0 ? <span className="tabs__count">{count}</span> : null}
    </button>
  );
}
